import React, { useEffect, useMemo, useState } from "react";
import { fromJS } from "immutable";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Backdrop from "../../../common-components/BackDrop";
import {
  ControlButton,
  FramesSettingHeader,
  FramesSettingWrapper,
  MobilePlayerControlLeftSection,
  MobilePlayerPopup,
  PlayerControlLeftSection,
  PlayerControlMiddleSection,
  PlayerControlRightSection,
  PlayerControlTextButton,
  PlayerControlsContainer,
  TimeScaleButton,
  TimeScaleContainer,
  TimelineExpand,
  TimelineFps,
} from "./timeline-playercontrols-components";
import Slider from "../../zoom/zoom-slider";
import { RULER_OPTIONS, TIMELINE_HEIGHT, TIMELINE_MODES } from "../timeline-constants";
import { STATIC_PATH, IS_LOCAL, ZOOM_BASE_URL } from "../../../constants/config";
import Typography from "../../../common-components/Typography";
import content from "../../../constants/content";
import { font } from "../../../constants/font";
import {
  secondsToTimestamp,
  buildLocalSmartfocus,
  applyAddZoomOutToZoomData,
  applyCheckZoomInToZoomData,
} from "../timeline-helper";
import useMobileDetection from "../../../helper/hooks/useMobileDetection";

import {
  setPlayAll,
  setPropertyPanel,
  setExpandPanel,
  setTimelineFPS,
  setToolbarIconDisable,
} from "../../../redux/actions/appUtils";
import { SelectedSliderType } from "../timeline-proptypes";
import CustomTooltipComponent from "../../../common-components/CustomTooltip";
import vmTheme from "../../../constants/theme";
import SelectableDropdown from "../../../common-components/SelectableDropdown";
import { updateProjectData, updateTimelineTime } from "../../../redux/actions/timelineUtils";
import DeleteModal from "../../../common-components/DeleteModal";
import triggerSimpleAjax from "../../../helper/httpHelper";

// Run both transformations to emulate reducer pipeline for zoomData only
const normalizeZoomData = (zoomData, { width, height, duration }) => {
  const withZoomOut = applyAddZoomOutToZoomData(zoomData, { width, height, duration });
  return applyCheckZoomInToZoomData(withZoomOut);
};

// Helpers for Smart Focus zoom comparison
const deriveWorkspaceItemIdFromTimelineId = (timelineId) => {
  if (typeof timelineId !== "string") return timelineId;
  const dashIndex = timelineId.lastIndexOf("-");
  return dashIndex === -1 ? timelineId : timelineId.slice(0, dashIndex);
};

const zoomValuesNearlyEqual = (a, b) => {
  if (typeof a === "number" && typeof b === "number") {
    return Math.abs(a - b) < 1e-6;
  }
  return a === b;
};

const isZoomStageInRange = (stage, videoStart, videoEnd) => {
  if (!stage || typeof stage.start !== "number" || typeof stage.end !== "number") {
    return false;
  }
  // Check if stage overlaps with the selected clip's media range
  return stage.end > videoStart && stage.start < videoEnd;
};

const zoomHasMismatch = (apiZoomData, localZoom, videoStart, videoEnd) => {
  const zoomIds = new Set([
    ...Object.keys(apiZoomData || {}),
    ...Object.keys(localZoom || {}),
  ]);

  const coreFields = ["start", "end", "x", "y", "height", "width"];
  const stages = ["zoomIn", "zoomOut"];

  let hasMismatch = false;

  zoomIds.forEach((zoomId) => {
    if (hasMismatch) return;

    const apiGroup = apiZoomData[zoomId] || {};
    const localGroup = localZoom[zoomId] || {};

    stages.forEach((stageName) => {
      if (hasMismatch) return;

      const apiStage = apiGroup[stageName] || {};
      const localStage = localGroup[stageName] || {};

      const apiInRange = isZoomStageInRange(apiStage, videoStart, videoEnd);
      const localInRange = isZoomStageInRange(localStage, videoStart, videoEnd);

      // Both stages outside the clip range -> ignore
      if (!apiInRange && !localInRange) {
        return;
      }

      // One stage exists in range but not the other -> mismatch
      if (apiInRange !== localInRange) {
        hasMismatch = true;
        return;
      }

      // Both exist within the clip range -> compare core fields
      coreFields.forEach((field) => {
        if (hasMismatch) return;

        const apiVal = apiStage[field];
        const localVal = localStage[field];

        if (!zoomValuesNearlyEqual(apiVal, localVal)) {
          hasMismatch = true;
        }
      });
    });
  });

  return hasMismatch;
};


const {
  TIMELINE,
  ADD_SUBTITLE,
  ADD_MEDIA,
  RECORD,
  TIMELINE_ZOOM,
  RESET_TIMELINE_ZOOM,
  TOGGLE_TIMELINE,
  PLAY_FROM_PLAYHEAD,
  PLAYHEAD_FORWARD,
  PLAYHEAD_BACKWARD,
  TOTAL_TIME,
  TIME_AT_PLAYHEAD,
  ADD,
  SPLIT,
  ADD_ZOOM,
  CLEAR_ALL,
  RESET_ALL
} = content;


const PlayerControls = (props) => {
  const dispatch = useDispatch();
  const appReducer = useSelector((state) => state.app);
  const isMobile = useMobileDetection();
  const isPlayAll = appReducer.get("isPlayAll");
  const theme = appReducer.get("theme");
  const projectDetails = useSelector((state) => state.projectDetails);
  const workspaceItems = projectDetails.get('workspaceItems');
  const projectDuration = projectDetails.get("duration");
  const timelineFPS = projectDetails.get("timelineFPS");
  const playIcon = isPlayAll ? vmTheme[theme].icons.pauseIcon : vmTheme[theme].icons.playIcon;
  const isTimelineMinimized = props.timelineHeight === TIMELINE_HEIGHT.minimum;
  const [fpsSettingOpen, setFpsSettingOpen] = useState(false);
  const [isSectionVisible, setSectionVisibility] = useState(false);
  const selectedItem = appReducer.get("selectedItems");
  const item = projectDetails.getIn(["workspaceItems", selectedItem.get(0)]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetTypographyModal, setShowResetTypographyModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // { workspaceItemId, zoomApiData } when mismatch exists
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        dispatch(setPlayAll(false));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  // Log SMART_FOCUS timelineId when smart focus panel is active
  const propertyComponent = appReducer.getIn(["propertyWindow", "component"]);
  const selectedSubtitles = appReducer.get("selectedSubtitles");

  const smartFocusTimelineId = useMemo(() => {
    if (!selectedSubtitles || !selectedSubtitles.size) {
      return null;
    }
    return selectedSubtitles.get(0);
  }, [selectedSubtitles]);

  useEffect(() => {
    const SMART_FOCUS_COMPONENTS = new Set([
    "SMART_FOCUS",
    "SMART_FOCUS_MOBILE",
    ]);
    if (!SMART_FOCUS_COMPONENTS.has(propertyComponent) || !smartFocusTimelineId) {
    setResetTarget(null);
    return;
    }
    const workspaceItemId = deriveWorkspaceItemIdFromTimelineId(smartFocusTimelineId);

    const workspaceItem = workspaceItems && workspaceItems.get ? workspaceItems.get(workspaceItemId) : null;

    if (!workspaceItem) {
      setResetTarget(null);
      return;
    }

    const videoStart = Number(workspaceItem.get("videoStart") || 0);
    const videoEnd = Number(
      workspaceItem.get("videoEnd") || projectDetails.get("duration") || 0
    );

    const workspaceItemData = workspaceItem.toJS ? workspaceItem.toJS() : workspaceItem;
    const zoomSrc = workspaceItemData && workspaceItemData.zoomSrc;
    const localZoomData = workspaceItemData && workspaceItemData.zoomData;

    if (!zoomSrc) {
      setResetTarget(null);
      return;
    }

    const normalizedPath = zoomSrc.startsWith("http")
      ? zoomSrc
      : `${ZOOM_BASE_URL}${zoomSrc.replace(/^\//, "")}`;

    triggerSimpleAjax(
      normalizedPath,
      "GET",
      false,
      {},
      (data) => {
        const meta = {
          width: projectDetails.get("width"),
          height: projectDetails.get("height"),
          duration: projectDetails.get("duration"),
        };

        const normalizedApiZoomData = normalizeZoomData(data, meta);
        const normalizedLocalZoomData = normalizeZoomData(localZoomData, meta);

        const apiZoomData = normalizedApiZoomData || {};
        const localZoom = normalizedLocalZoomData || {};

        const hasMismatch = zoomHasMismatch(apiZoomData, localZoom, videoStart, videoEnd);

        setResetTarget(
          hasMismatch ? { workspaceItemId, zoomApiData: normalizedApiZoomData } : null
        );
      },
      (error) => {
        console.error("SMART FOCUS (PlayerControls) — zoom fetch error:", error);
        setResetTarget(null);
      },
      false // disable token/credentials for static JSON
    );
  }, [propertyComponent, smartFocusTimelineId, selectedSubtitles, workspaceItems, projectDetails]);

  /** @type {{ playheadSlider: Slider, rulerSlider: Slider, stepperInterval: number }} */
  const { playheadSlider, rulerSlider, stepperInterval } = props;

  const playheadText = useMemo(() => {
    let playheadPosition = playheadSlider.enterStart.position;
    if (playheadSlider.enterStart.trackPosition) {
      playheadPosition = playheadSlider.enterStart.trackPosition;
    }

    const seconds = playheadPosition.step * stepperInterval;

    return secondsToTimestamp({ seconds, mode: "playercontrol" });
  }, [playheadSlider, stepperInterval]);

  const projectDurationText = useMemo(() => {
    let projectDurationPosition = rulerSlider.exitEnd.position;
    if (rulerSlider.exitEnd.trackPosition) {
      projectDurationPosition = rulerSlider.exitEnd.trackPosition;
    }

    const seconds = projectDurationPosition.step * stepperInterval;
    const timestamp = secondsToTimestamp({ seconds, mode: "playercontrol" });

    return ` / ${timestamp}`;
  }, [rulerSlider, stepperInterval]);

  const handlePlayClick = () => {
    if (!isPlayAll) {
      const playheadPosition = playheadSlider.enterStart.position;
      const playheadSeconds = playheadPosition.step * stepperInterval;
      const isFullPreview = Number(playheadSeconds.toFixed(2)) >= Number(projectDuration.toFixed(2));
      dispatch(setPlayAll(true, isFullPreview));
    } else {
      dispatch(setPlayAll(false));
    }
  };

  const changeFpsRate = (fps) => {
    const fpsValue = Number(fps)
    dispatch(setTimelineFPS({ fps: fpsValue }));
    dispatch(updateProjectData({
      timelineFPS: fpsValue,
      timelineInterval: 1 / fpsValue
    }))
  }

  const handleShortcuts = (shortcutAction) => {
    const { adjustPlayheadStepBy, selectedSliders } = props;
    switch (shortcutAction) {
      case "START_PLAY_PLAYHEAD":
        if (!appReducer.get("isCropping")) {
          handlePlayClick();
        }
        break;
      case "MOVE_PLAYHEAD_1S_LEFT":
        // Prevent action if item is selected.
        !selectedSliders.length && adjustPlayheadStepBy(-10);
        break;
      case "MOVE_PLAYHEAD_1S_RIGHT":
        // Prevent action if item is selected.
        !selectedSliders.length && adjustPlayheadStepBy(10);
        break;
      case "MOVE_PLAYHEAD_5S_LEFT":
        // Prevent action if item is selected.
        !selectedSliders.length && adjustPlayheadStepBy(-50);
        break;
      case "MOVE_PLAYHEAD_5S_RIGHT":
        // Prevent action if item is selected.
        !selectedSliders.length && adjustPlayheadStepBy(50);
        break;
      default:
        break;
    }
  };

  const handleDropdownClick = () => {
    const { onDropdownClick } = props;
    if (onDropdownClick) {
      onDropdownClick(); // Invoke the callback function passed from the parent
    }
  };

  const toggleSection = () => {
    const { setOuterLayerVisible } = props;
    setSectionVisibility(!isSectionVisible);
    setOuterLayerVisible(isSectionVisible);
    handleDropdownClick();
    if (isMobile) {
      dispatch(setToolbarIconDisable(!isSectionVisible))
    }
  };

  useEffect(() => {
    const { shortcutName } = props;
    if (shortcutName && shortcutName !== "") {
      handleShortcuts(shortcutName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.shortcutName]);

  const openUploadPanel = () => {
    // toggleSection();
    dispatch(setPropertyPanel(content.UPLOAD));
    dispatch(setExpandPanel(true));
  };

  const handleOnSave = () => {
    props.zoomClear();
    setShowDeleteModal(false);
  }

  const handleClearAllModal = () => {
    setShowDeleteModal(true);
  }

  const handleResetTypographyModal = () => {
    setShowResetTypographyModal(true);
  }

  const handleResetTypographyConfirm = () => {
    try {
      if (!resetTarget || !resetTarget.workspaceItemId || !resetTarget.zoomApiData) {
        setShowResetTypographyModal(false);
        return;
      }

      const { workspaceItemId, zoomApiData } = resetTarget;

      const itemToReset = projectDetails.getIn(["workspaceItems", workspaceItemId]);
      if (!itemToReset) {
        setShowResetTypographyModal(false);
        return;
      }

      const updatedZoomData = zoomApiData;

      // Create a projected state with updated zoomData for building localZoom
      const updatedProject = projectDetails.setIn(
        ["workspaceItems", workspaceItemId, "zoomData"],
        fromJS(updatedZoomData)
      );

      const toUpdate = [
        {
          id: workspaceItemId,
          container: "workspaceItems",
          toUpdate: {
            zoomData: updatedZoomData,
          },
        },
      ];

      // Apply zoomData changes via timeline update
      dispatch(updateTimelineTime({ toUpdate }));

      // Rebuild localZoom and sync into projectDetails
      const projectWithLocalZoom = buildLocalSmartfocus(updatedProject);
      const updatedLocalZoom = projectWithLocalZoom.get("localZoom");
      if (updatedLocalZoom) {
        dispatch(updateProjectData({ localZoom: updatedLocalZoom }));
      }
    } finally {
      setShowResetTypographyModal(false);
    }
  };

  const { min, maxScaleDuration } = RULER_OPTIONS.timeScale;
  let { max } = RULER_OPTIONS.timeScale;
  if (projectDuration <= maxScaleDuration) {
    max = RULER_OPTIONS.timeScale.default;
  }

  const toggleSubtitleMode = (event) => {
    if (isMobile) {
      toggleSection();
    }
    props.toggleSubtitleMode(event);
  };

  const splitHandle = () => {
    if (isMobile) {
      toggleSection();
    }
    props.splitHandler()
  }

  const leftSectionButtons = [];
  if (props.timelineMode === TIMELINE_MODES.MAIN) {
    leftSectionButtons.push(
      <PlayerControlTextButton
        key="add-media"
        className={isPlayAll ? "pc--is-disabled" : ""}
        onClick={openUploadPanel}
      >
        <img
          draggable="false"
          alt="add media"
          src={`${STATIC_PATH}${vmTheme[theme].icons.addMedia}`}
        />
        <Typography
          as="span"
          color={vmTheme[theme].primaryColor}
          content={ADD_MEDIA}
          font={font.normalMini}
          cursor={"pointer"}
          enableTrim={true}
          maxWidth={"75px"}
        />
        
      </PlayerControlTextButton>
    );
    if (IS_LOCAL) {
      leftSectionButtons.push(
        <PlayerControlTextButton
          key="record"
          className={
            isPlayAll
              ? "pc--is-disabled"
              : "create_button chrome-vmaker-ext-btn"
          }
          onClick={() => {
            if (document.getElementsByName("vmaker-container").length) {
              window.location = "Vmaker://open";
            }
          }}
        >
          <img
            draggable="false"
            alt="record"
            src={`${STATIC_PATH}${vmTheme[theme].icons.recordIcon}`}
          />
          <Typography
            as="span"
            color={vmTheme[theme].primaryColor}
            content={RECORD}
            font={font.normalMini}
            cursor={"pointer"}
            enableTrim={true}
            maxWidth={"75px"}
          />
        </PlayerControlTextButton>
      );
    }
    leftSectionButtons.push(
      <PlayerControlTextButton
        key="split"
        className={(isPlayAll || (item && item.get("isLock"))) ? "pc--is-disabled" : ""}
        onClick={splitHandle}
      >
        <img
          draggable="false"
          alt="split"
          src={`${STATIC_PATH}${vmTheme[theme].icons.splitIcon}`}
        />
        <Typography
          as="span"
          color={vmTheme[theme].primaryColor}
          content={SPLIT}
          font={font.normalMini}
          cursor={"pointer"}
          enableTrim={true}
          maxWidth={"75px"}
        />
      </PlayerControlTextButton>
    );
  } else if (props.timelineMode === TIMELINE_MODES.SUBTITLE) {
    leftSectionButtons.push(
      <PlayerControlTextButton
        key="back-timeline"
        className="timeline-btn-highlight"
        onClick={toggleSubtitleMode}
      >
        <img
          draggable="false"
          alt="back to timeline"
          src={`${STATIC_PATH}${vmTheme[theme].icons.timelineBack}`}
        />
        <Typography
          as="span"
          color={vmTheme[theme].primaryColor}
          content={TIMELINE}
          font={font.normalMini}
          cursor={"pointer"}
          enableTrim={true}
          maxWidth={"75px"}
        />
      </PlayerControlTextButton>
    );
    leftSectionButtons.push(
      <PlayerControlTextButton
        key="add-subtitle"
        className={isPlayAll ? "pc--is-disabled" : ""}
        onClick={props.addSubtitle}
      >
        <img
          draggable="false"
          alt="split"
          src={`${STATIC_PATH}${vmTheme[theme].icons.addMedia}`}
        />
        <Typography
          as="span"
          color={vmTheme[theme].primaryColor}
          content={ADD_SUBTITLE}
          font={font.normalMini}
          cursor={"pointer"}
          enableTrim={true}
          maxWidth={"86px"}
        />
      </PlayerControlTextButton>
    );
    leftSectionButtons.push(
      <PlayerControlTextButton
        key="split"
        className={(isPlayAll || (item && item.get("isLock"))) ? "pc--is-disabled" : ""}
        onClick={props.splitHandler}
      >
        <img
          draggable="false"
          alt="split"
          src={`${STATIC_PATH}${vmTheme[theme].icons.splitIcon}`}
        />
        <Typography
          as="span"
          color={vmTheme[theme].primaryColor}
          content={SPLIT}
          font={font.normalMini}
          cursor={"pointer"}
          enableTrim={true}
          maxWidth={"75px"}
        />
      </PlayerControlTextButton>
    );
  } else if (props.timelineMode === TIMELINE_MODES.SMART_FOCUS) {
    leftSectionButtons.push(
      <PlayerControlTextButton
        key="add-zoom"
        className={isPlayAll ? "pc--is-disabled" : ""}
        onClick={props.addZoom}
        style={{ border: "1px solid #E3E4E8", borderRadius: "8px", padding: "4.5px 6px" }}
      >
        <img
          draggable="false"
          alt="Add zoom"
          src={`${STATIC_PATH}${vmTheme[theme].icons.addMedia}`}
        />
        <Typography
          as="span"
          color={vmTheme[theme].primaryColor}
          content={ADD_ZOOM}
          font={font.mediumMiniBase}
          cursor={"pointer"}
          enableTrim={true}
          maxWidth={"86px"}
        />
      </PlayerControlTextButton>
    );
    leftSectionButtons.push(
      <PlayerControlTextButton
        key="clear-all"
        className={isPlayAll ? "pc--is-disabled" : ""}
        onClick={handleClearAllModal}
      >
        <img
          draggable="false"
          alt="Clear all"
          src={`${STATIC_PATH}${vmTheme[theme].icons.clearAllIcon}`}
        />
        <Typography
          as="span"
          color={vmTheme[theme].primaryColor}
          content={CLEAR_ALL}
          font={font.mediumMiniBase}
          cursor={"pointer"}
          enableTrim={true}
          maxWidth={"86px"}
        />
      </PlayerControlTextButton>
      
    );
  
      if (resetTarget) {
      leftSectionButtons.push(
        <PlayerControlTextButton
          key="reset-all"
          className={isPlayAll ? "pc--is-disabled" : ""}
          onClick={handleResetTypographyModal}
        >
          <img
            draggable="false"
            alt="Reset"
            src={`${STATIC_PATH}${vmTheme[theme].icons.resetIcon}`}
          />
          <Typography
            as="span"
            color={vmTheme[theme].primaryColor}
            content={RESET_ALL}
            font={font.mediumMiniBase}
            cursor={"pointer"}
            enableTrim={true}
            maxWidth={"115px"}
          />
        </PlayerControlTextButton>
      );
    }
  }



  return (
    <PlayerControlsContainer isSectionVisible={isSectionVisible}>
      {props.isShowPlayControls && (
        <>
          {isMobile && (
            <>
              <img src={`${STATIC_PATH}add.svg`} height="24px" width="24px" cursor={"pointer"} onClick={toggleSection} alt="addIcon" />
              {isSectionVisible && (
                <>
                  <Backdrop background="#00000061 0% 0% no-repeat padding-box" zIndex={98} onClick={toggleSection} />
                  <MobilePlayerControlLeftSection>
                    <MobilePlayerPopup>
                      <Typography
                        content={ADD}
                        color={vmTheme[theme].panelPrimaryColor}
                        padding="4px 4px 4px 0px"
                        width="100%"
                        align="center"
                        display="inline-block"
                        font="normal normal 600 16px/14px Poppins;"
                      />
                      <img src={`${STATIC_PATH}cross.svg`} height="20px" width="20px" cursor={"pointer"} onClick={toggleSection} alt="cross-icon" className="sm-close-icon" />
                      {leftSectionButtons}
                    </MobilePlayerPopup>
                  </MobilePlayerControlLeftSection>
                </>
              )}
            </>
          )}
          {!isMobile && (
            <PlayerControlLeftSection>
              {leftSectionButtons}
            </PlayerControlLeftSection>
          )}
          <PlayerControlMiddleSection>
            {!isMobile && <ControlButton
              className={isPlayAll ? "pc--is-disabled" : ""}
              onClick={() => props.onSeekClick({ direction: "left" })}
            >
              <CustomTooltipComponent
                tooltipId={"player-backward-tooltip"}
                dataTooltip={PLAYHEAD_BACKWARD}
                tooltipPosition="top"
              >
                <img
                  data-tooltip-id={"player-backward-tooltip"}
                  draggable="false"
                  alt="backward"
                  src={`${STATIC_PATH}${vmTheme[theme].icons.playerBackward}`}
                />
              </CustomTooltipComponent>
            </ControlButton>}
            <CustomTooltipComponent
              tooltipId={"play/pause-tooltip"}
              dataTooltip={PLAY_FROM_PLAYHEAD}
              tooltipPosition="top"
            >
              <ControlButton
                data-tooltip-id={"play/pause-tooltip"}
                onClick={handlePlayClick}
                onTouchStart={(e) => {
                  e.preventDefault(); // Prevent default behavior for touch events
                  handlePlayClick(); // Trigger the click handler
                }}
                isMobile={isMobile}
              >
                <img
                  draggable="false"
                  alt="play/pause"
                  src={`${STATIC_PATH}${playIcon}-player.svg`}
                />
              </ControlButton>
            </CustomTooltipComponent>
            <div className="pc--timestamp">
              <span>
                <CustomTooltipComponent
                  tooltipId={"duration-tooltip"}
                  dataTooltip={TIME_AT_PLAYHEAD}
                  tooltipPosition="top"
                >
                  <Typography
                    as="span"
                    data-tooltip-id={"duration-tooltip"}
                    color={vmTheme[theme].primaryColor}
                    innerContent={playheadText}
                    font={font.normalMini}
                  />
                </CustomTooltipComponent>
                <CustomTooltipComponent
                  tooltipId={"duration-1-tooltip"}
                  dataTooltip={TOTAL_TIME}
                  tooltipPosition="top"
                >
                  <Typography
                    as="span"
                    data-tooltip-id={"duration-1-tooltip"}
                    color={vmTheme[theme].secondaryColor}
                    innerContent={projectDurationText}
                    font={font.normalMini}
                  />
                </CustomTooltipComponent>
              </span>
            </div>
            {!isMobile && <ControlButton
              className={isPlayAll ? "pc--is-disabled" : ""}
              onClick={() => props.onSeekClick({ direction: "right" })}
            >
              <CustomTooltipComponent
                tooltipId={"player-forward-tooltip"}
                dataTooltip={PLAYHEAD_FORWARD}
                tooltipPosition="top"
              >
                <img
                  data-tooltip-id={"player-forward-tooltip"}
                  draggable="false"
                  alt="forward"
                  src={`${STATIC_PATH}${vmTheme[theme].icons.playerForward}`}
                />
              </CustomTooltipComponent>
            </ControlButton>}
          </PlayerControlMiddleSection>
          <PlayerControlRightSection>
            <TimeScaleContainer>
              {!isMobile &&
                <>
                  <TimeScaleButton
                    className={`pc--scale-minus ${isPlayAll ? "pc--is-disabled" : ""}`}
                    onClick={() =>
                      props.onTimeScaleChange(
                        null,
                        props.timeScale - RULER_OPTIONS.timeScale.buttonStep
                      )
                    }
                  >
                    <img
                      draggable="false"
                      alt="zoom in timeline"
                      src={`${STATIC_PATH}${vmTheme[theme].icons.timeScaleMinus}`}
                    />
                  </TimeScaleButton>
                  <CustomTooltipComponent
                    tooltipId={"zoom-slider-tooltip"}
                    dataTooltip={TIMELINE_ZOOM}
                    tooltipPosition="top"
                  >
                    <Slider
                      tooltipId={"zoom-slider-tooltip"}
                      className={isPlayAll ? "pc--is-disabled" : ""}
                      width={"84px"}
                      border={`1px solid ${vmTheme[theme].timelineSliderBg}`}
                      borderRadius={"2px"}
                      background={vmTheme[theme].timelineSliderBg}
                      margin={"0"}
                      thumb={{
                        background: `${vmTheme[theme].polarColor} 0% 0% no-repeat padding-box`,
                        border: `2px solid ${vmTheme[theme].secondaryBorderColor}`,
                        height: "14px",
                        width: "14px",
                        hoverBG: `${vmTheme[theme].secondaryBorderColor}`,
                        hoverBorder: `1.7px solid ${vmTheme[theme].secondaryBorderColor}`,
                      }}
                      onChangeSliderValue={props.onTimeScaleChange}
                      value={props.timeScale}
                      height="4px"
                      min={min}
                      max={max}
                      inputMin={min}
                      inputMax={max}
                      step={RULER_OPTIONS.timeScale.step}
                      progressBackground={vmTheme[theme].sliderDefaultValue}
                      isChangeBg={true}
                    />
                  </CustomTooltipComponent>
                  <TimeScaleButton
                    className={`pc--scale-plus ${isPlayAll ? "pc--is-disabled" : ""}`}
                    onClick={() =>
                      props.onTimeScaleChange(
                        null,
                        props.timeScale + RULER_OPTIONS.timeScale.buttonStep
                      )
                    }
                  >
                    <img
                      draggable="false"
                      alt="zoom out timeline"
                      src={`${STATIC_PATH}${vmTheme[theme].icons.timeScalePlus}`}
                    />
                  </TimeScaleButton>
                </>}
              <CustomTooltipComponent
                tooltipId={"zoom-reset-tooltip"}
                dataTooltip={RESET_TIMELINE_ZOOM}
                tooltipPosition="top"
              >
                <TimeScaleButton
                  data-tooltip-id={"zoom-reset-tooltip"}
                  className={`pc--scale-reset ${isPlayAll ? "pc--is-disabled" : ""
                    } ${props.timeScale ? "allowed" : "not-allowed"}`}
                  onClick={() => {
                    if (props.timeScale) {
                      props.onTimeScaleChange(null, 0);
                    }
                  }}
                >
                  <img
                    draggable="false"
                    alt="reset timeline scale"
                    src={`${STATIC_PATH}${vmTheme[theme].icons.timeScaleReset}`}
                  />
                </TimeScaleButton>
              </CustomTooltipComponent>
            </TimeScaleContainer>
            {!isMobile &&
              <TimelineFps
                className={isPlayAll ? "pc--is-disabled" : ""}
                onClick={() => setFpsSettingOpen(true)}
                isActive={fpsSettingOpen}
              >
                <img
                  draggable="false"
                  alt="toggle timeline height"
                  src={`${STATIC_PATH}${vmTheme[theme].icons.fpsIcon}`}
                />
              </TimelineFps>
            }
            <CustomTooltipComponent
              tooltipId={"toggle-timeline-tooltip"}
              dataTooltip={TOGGLE_TIMELINE}
              tooltipPosition="top-right"
            >
              <TimelineExpand
                data-tooltip-id={"toggle-timeline-tooltip"}
                className={isPlayAll ? "pc--is-disabled" : ""}
                $type={isTimelineMinimized ? "expand" : ""}
                onClick={() =>
                  props.stopTimelineResize({
                    clickType: isTimelineMinimized ? "expand" : "minimize",
                  })
                }
              >
                <img
                  draggable="false"
                  alt="toggle timeline height"
                  src={`${STATIC_PATH}${vmTheme[theme].icons.timelineMinimize}`}
                />
              </TimelineExpand>
            </CustomTooltipComponent>
            {
              fpsSettingOpen &&
              <FramesSettingWrapper>
                <Backdrop
                  width={"100vw"}
                  height={"100vh"}
                  zIndex="-1"
                  onClick={() => { setFpsSettingOpen(false) }}
                />
                <FramesSettingHeader>
                  <Typography
                    content={"Frames per second"}
                    color={vmTheme[theme].tertiaryColor}
                    font={font.normalMini_14}
                  />
                  <SelectableDropdown onAction={(e) => changeFpsRate(e)} dropDownValue={[60, 50, 30, 25, 24]} defaultValue={timelineFPS} />
                </FramesSettingHeader>

              </FramesSettingWrapper>
            }
          </PlayerControlRightSection>
        </>
      )}
      {
        showDeleteModal &&
        <DeleteModal text={"Clear all focus"} subText={"Are you sure want to clear all generated focus?"} confirmText={"Clear all"} onCloseModal={() => setShowDeleteModal(false)} onSave={handleOnSave} />
      }
      {
        showResetTypographyModal &&
        <DeleteModal
          isOpen={showResetTypographyModal}
          onCloseModal={() => setShowResetTypographyModal(false)}
          subText={"Your manual zoom points will be cleared."}
          text={"Reset Smart Focus"}
          confirmText={"Reset"}
          cancelText={"Cancel"}
          onSave={handleResetTypographyConfirm}
          bgColor={vmTheme[theme].secondaryBorderColor }
        />
      } 
    </PlayerControlsContainer>
  );
};

PlayerControls.propTypes = {
  timelineMode: PropTypes.number,
  toggleSubtitleMode: PropTypes.func,
  timeScale: PropTypes.number,
  stepperInterval: PropTypes.number,
  onTimeScaleChange: PropTypes.func,
  timelineHeight: PropTypes.number,
  stopTimelineResize: PropTypes.func,
  playheadSlider: PropTypes.object,
  rulerSlider: PropTypes.object,
  adjustPlayheadStepBy: PropTypes.func,
  shortcutName: PropTypes.string,
  selectedSliders: PropTypes.arrayOf(SelectedSliderType),
  onSeekClick: PropTypes.func,
  isShowPlayControls: PropTypes.bool,
  splitHandler: PropTypes.func,
  addSubtitle: PropTypes.func,
  onDropdownClick: PropTypes.func,
  setOuterLayerVisible: PropTypes.func,
  addZoom: PropTypes.func,
  zoomClear: PropTypes.func,
  // onFpsControlClick: PropTypes.func
};

export default PlayerControls;